import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { Users } from "lucide-react"
import { Fragment, useState } from "react"
import { AddSupplierEmailModal } from "./AddSupplierEmailModal"
import type { InquiryDraftItem } from "./inquiryDraftMock"
import { Row } from "./RecipientEditor"

export function RecipientList({
  inquiries,
  onEmailAdded,
  onRecipientsChanged,
  pendingAddresses,
}: {
  inquiries: InquiryDraftItem[]
  // First email for a supplier with none yet (routes through AddSupplierEmailModal,
  // which also writes the supplier's master-data email — see RecipientEditor).
  onEmailAdded?: (inquiryId: string, email: string) => void
  // Any other To/Cc add, edit or remove once a supplier has at least one address.
  onRecipientsChanged?: (
    inquiryId: string,
    field: "recipientsTo" | "recipientsCc",
    next: string[],
    address: string,
  ) => void
  // "<inquiryId>:<field>" -> the address currently mid-PATCH for it — see
  // InquiryDraftPage.
  pendingAddresses?: Map<string, string>
}) {
  const { translate } = useTranslation()
  const [showAll, setShowAll] = useState(false)
  const [activeInquiry, setActiveInquiry] = useState<InquiryDraftItem | null>(
    null,
  )
  const visible = showAll ? inquiries : inquiries.slice(0, 4)

  return (
    <div className="min-w-0 rounded-lg border border-grey4 bg-grey2 px-4 py-3.5">
      <div className="mb-3 flex items-center gap-2 text-[11.5px] text-grey5">
        <Users className="h-3.5 w-3.5 text-purple1" />
        {translate("MC_INQ_DRAFT_BULK_RCP_LEAD")}
      </div>
      {/*
        One shared grid (not a per-row flex/grid container) so the name column's
        width is the widest supplier name across the WHOLE list, not a guessed
        fixed width — a fixed cap either truncated names that would've fit, or
        wasted space when every name was short. Each supplier contributes its
        name + email cells as two direct grid children (via Fragment) so they
        land in the shared column tracks; a max-width safety net still caps a
        pathological outlier name instead of blowing out the whole column.
      */}
      <div className="grid min-w-0 grid-cols-[max-content_1fr] items-start gap-x-3">
        {visible.map((s, i) => {
          const toEmpty = s.contactEmails.length === 0
          const rowBorder = cn("py-2", i > 0 && "border-t border-white/5")
          return (
            <Fragment key={s.id}>
              <span
                className={cn(
                  "max-w-88 truncate pt-1 text-[11.5px] text-white",
                  rowBorder,
                )}
                title={s.supplierName}
              >
                {s.supplierName}
              </span>
              <div
                className={cn(
                  // min-w-0 overrides a grid item's default `min-width: auto`, which
                  // otherwise floors this column at its unwrapped content width (the
                  // widest chip run) and pushes the whole card past the resizable
                  // panel's edge instead of letting `flex-wrap` actually wrap.
                  "flex min-w-0 flex-wrap items-center justify-end gap-2",
                  rowBorder,
                )}
              >
                {toEmpty && (
                  <span className="font-mono text-[11px] italic text-warning">
                    {translate("MC_INQ_DRAFT_NO_CONTACT")}
                  </span>
                )}
                <Row
                  compact
                  list={s.contactEmails}
                  onAdd={(addr) =>
                    onRecipientsChanged?.(
                      s.id,
                      "recipientsTo",
                      [...s.contactEmails, addr],
                      addr,
                    )
                  }
                  onRemove={(addr) =>
                    onRecipientsChanged?.(
                      s.id,
                      "recipientsTo",
                      s.contactEmails.filter((a) => a !== addr),
                      addr,
                    )
                  }
                  onAddMissingMasterDataEmail={
                    onEmailAdded ? () => setActiveInquiry(s) : undefined
                  }
                  pendingAddress={pendingAddresses?.get(`${s.id}:recipientsTo`)}
                />
              </div>
            </Fragment>
          )
        })}
      </div>
      {inquiries.length > 4 && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="pt-2 text-[11.5px] font-semibold text-grey5 hover:text-white"
        >
          {showAll
            ? translate("MC_INQ_DRAFT_SHOW_FEWER")
            : translate("MC_INQ_DRAFT_SHOW_ALL_RCP", { n: inquiries.length })}
        </button>
      )}
      {activeInquiry && (
        <AddSupplierEmailModal
          open={!!activeInquiry}
          onOpenChange={(open) => {
            if (!open) setActiveInquiry(null)
          }}
          supplierId={activeInquiry.supplierId}
          supplierName={activeInquiry.supplierName}
          supplierNumber={activeInquiry.supplierNumber}
          onSaved={(email) => {
            onEmailAdded?.(activeInquiry.id, email)
            setActiveInquiry(null)
          }}
        />
      )}
    </div>
  )
}
